import { merge } from '../helper';
import isEmptyObject from '../general/isEmpty/isEmptyObject';

import exLanguage from '../language';
import { numberToTime } from '../utils/time';
import * as exButton from '../components/button';
import * as exInput from '../forms/input';
import * as exRange from '../forms/range';
import * as exElement from '../utils/element';

// ----------------------------------------------------------------------------

type TypeControlCallbackParam = Record<string, any> & {
    fps?: number;
    isPlaying?: boolean;
    time?: any;
};

type TypeControlCallback = (...args: any[]) => void;

type TypeControlConfig = Record<string, any> & {
    definition?: Record<string, any>;
    duration?: number;
    fastBackward?: Record<string, any>;
    fastForward?: Record<string, any>;
    fps?: TypeControlConfig;
    framePerSecond?: number;
    fullscreen?: boolean;
    fnCallback?: TypeControlCallback | ((param: TypeControlCallbackParam) => void);
    input?: TypeControlConfig & {
        editor?: boolean;
    };
    minus?: TypeControlConfig;
    play?: TypeControlConfig;
    plus?: TypeControlConfig;
    range?: TypeControlConfig;
    stepBackward?: TypeControlConfig;
    stepForward?: TypeControlConfig;
    stop?: TypeControlConfig;
    time?: boolean;
    volume?: boolean;
};

type TypeControlBuild = {
    element: HTMLElement;
    elements: Record<string, any>;
    getValue: () => any;
    play: () => void;
    setValue: (value: any) => void;
    stop: () => void;
    toggle: () => void;
};

// ----------------------------------------------------------------------------

function start(config: TypeControlConfig = {}): TypeControlBuild {
    // Manager Control
    const managerControl = manager(config);

    // Return
    return managerControl;
}

// ----------------------------------------------------------------------------

const manager = function (config: TypeControlConfig = {}): TypeControlBuild {
    // Source
    const src: Record<string, any> = {
        elements: {},
        config: {
            isPlaying: false,
        },
    };

    // Default Config
    const defConfig: TypeControlConfig = {
        duration: 0,
    };

    // ------------------------------------------------------------------------

    function init(): void {
        // Merge
        merge(defConfig, config);

        // Start
        start(defConfig);
    }

    function start(config: TypeControlConfig = {}): void {
        // Create element (control)
        const elControl = exElement.create({
            merge: config,
            class: {
                new: 'control',
                add: 'flex-horizontal',
            },
        });
        src.element = elControl;
        src.elements.element = elControl;

        // Controller
        controller.start(config);
    }

    // ------------------------------------------------------------------------

    const controller = (function (): any {
        function start(config: TypeControlConfig = {}): void {
            play(config.play);
            stop(config.stop);
            fastBackward(config.fastBackward);
            stepBackward(config.stepBackward);
            stepForward(config.stepForward);
            fastForward(config.fastForward);
            range(config.range);
            time(config.time);
            volume.create(config.volume);
            framePerSecond.create(config.fps);
            definition(config.definition);
            fullscreen(config.fullscreen);
        }

        // --------------------------------------------------------------------

        function play(config: TypeControlConfig = {}): void {
            // If not using play
            if (isEmptyObject(config)) {
                return;
            }

            // Create element (button) (play)
            const button = exButton.create({
                merge: config,
                class: {
                    add: 'play',
                },
                attr: {
                    title: exLanguage.line('controller.play'),
                },
                icon: 'fa-solid fa-play',
            });
            src.element.appendChild(button.element);
            src.elements.play = button;

            // Create event (click)
            button.element.addEventListener('click', function (evt): any {
                // Toggle
                player.toggle();

                // If using function callback
                if (typeof config.fnCallback === 'function') {
                    const fnCallback = config.fnCallback;
                    const param: TypeControlCallbackParam = {
                        isPlaying: src.config.isPlaying,
                    };

                    // Function Callback
                    fnCallback.call(this, evt, param);
                }
            });
        }

        function stop(config: TypeControlConfig = {}): void {
            // If not using stop
            if (isEmptyObject(config)) {
                return;
            }

            // Create element (button) (stop)
            const button = exButton.create({
                merge: config,
                class: {
                    add: 'stop',
                },
                attr: {
                    title: exLanguage.line('controller.stop'),
                },
                icon: 'fa-solid fa-stop',
            });
            src.element.appendChild(button.element);
            src.elements.stop = button;

            // Create event (click)
            button.element.addEventListener('click', function (evt): any {
                // Stop
                player.stop();

                // If using function callback
                if (typeof config.fnCallback === 'function') {
                    const fnCallback = config.fnCallback;
                    const param: TypeControlCallbackParam = {
                        isPlaying: src.config.isPlaying,
                    };

                    // Function Callback
                    fnCallback.call(this, evt, param);
                }
            });
        }

        // --------------------------------------------------------------------

        function fastBackward(config: TypeControlConfig = {}): void {
            // If not using fast backward
            if (isEmptyObject(config)) {
                return;
            }

            // Create button (fast backward)
            const button = exButton.create({
                merge: config,
                class: {
                    add: 'fast-backward',
                },
                attr: {
                    title: exLanguage.line('controller.fast_backward'),
                },
                icon: 'fa-solid fa-fast-backward',
            });
            src.element.appendChild(button.element);
            src.elements.fastBackward = button;
        }

        function fastForward(config: TypeControlConfig = {}): void {
            // If not using fast forward
            if (isEmptyObject(config)) {
                return;
            }

            // Create button (fast forward)
            const button = exButton.create({
                merge: config,
                class: {
                    add: 'fast-forward',
                },
                attr: {
                    title: exLanguage.line('controller.fast_forward'),
                },
                icon: 'fa-solid fa-fast-forward',
            });
            src.element.appendChild(button.element);
            src.elements.fastForward = button;
        }

        function stepBackward(config: TypeControlConfig = {}): void {
            // If not using step backward
            if (isEmptyObject(config)) {
                return;
            }

            // Create button (step backward)
            const button = exButton.create({
                merge: config,
                class: {
                    add: 'step-backward',
                },
                attr: {
                    title: exLanguage.line('controller.step_backward'),
                },
                icon: 'fa-solid fa-step-backward',
            });
            src.element.appendChild(button.element);
            src.elements.stepBackward = button;
        }

        function stepForward(config: TypeControlConfig = {}): void {
            // If not using step forward
            if (isEmptyObject(config)) {
                return;
            }

            // Create button (step forward)
            const button = exButton.create({
                merge: config,
                class: {
                    add: 'step-forward',
                },
                attr: {
                    title: exLanguage.line('controller.step_forward'),
                },
                icon: 'fa-solid fa-step-forward',
            });
            src.element.appendChild(button.element);
            src.elements.stepForward = button;
        }

        // --------------------------------------------------------------------

        function range(config: TypeControlConfig = {}): void {
            // If not using range
            if (isEmptyObject(config)) {
                return;
            }

            // Create element (control range)
            const elControlRange = exElement.create({
                class: {
                    new: 'control-range',
                },
            });
            src.element.appendChild(elControlRange);
            src.elements.controlRange = elControlRange;

            // Create element (range)
            const range = exRange.create({
                merge: config,
                max: defConfig.duration,
            });
            elControlRange.appendChild(range.element);
            src.elements.range = range;

            // If using function fnCallback
            if (typeof config.fnCallback === 'function') {
                // Create event (click)
                const fnCallback = config.fnCallback;

                range.element.addEventListener('click', function (this: HTMLElement, evt: Event): any {
                    const value = (evt.currentTarget as HTMLInputElement).value;
                    src.config.time = value;

                    const param: TypeControlCallbackParam = {
                        isPlaying: src.config.isPlaying,
                        time: src.config.time,
                    };

                    // Function Callback
                    fnCallback.call(this, evt, param);
                });

                // Create event (input)
                range.element.addEventListener('input', function (this: HTMLElement, evt: Event): any {
                    const value = (evt.currentTarget as HTMLInputElement).value;
                    src.config.time = value;

                    const param: TypeControlCallbackParam = {
                        isPlaying: src.config.isPlaying,
                        time: src.config.time,
                    };

                    // Function Callback
                    fnCallback.call(this, evt, param);
                });
            }
        }

        function time(config: TypeControlConfig | boolean = {}): void {
            // If not using time
            if (config !== true) {
                return;
            }

            //
            const res =
                numberToTime('0', { format: 'MM:SS' }) +
                ' / ' +
                numberToTime(defConfig.duration ?? 0, { format: 'MM:SS' });

            // Create element (time)
            const elTime = exElement.create({
                merge: config,
                class: {
                    new: 'control-time',
                },
                attr: {
                    title: exLanguage.line('general.time'),
                },
                html: res,
            });
            src.element.appendChild(elTime);
            src.elements.time = elTime;

            // Function Set Value
            (elTime as any).setValue = function (): any {
                const value = src.config.time;
                const time =
                    numberToTime(Math.floor(value), { format: 'MM:SS' }) +
                    ' / ' +
                    numberToTime(defConfig.duration ?? 0, { format: 'MM:SS' });
                elTime.innerHTML = time;
            };
        }

        // --------------------------------------------------------------------

        const volume = (function (): any {
            function create(config: TypeControlConfig | boolean = {}): void {
                // If not using time
                if (config !== true) {
                    return;
                }

                // Init
                src.config.mute = false;

                // Create element (button) (volume)
                const button = exButton.create({
                    merge: config,
                    class: {
                        add: 'volume',
                    },
                    attr: {
                        title: 'mute',
                    },
                    icon: 'fa-solid fa-volume-up',
                });
                src.element.appendChild(button.element);
                src.elements.volume = button;

                // Create event (click)
                button.element.addEventListener('click', function (): any {
                    src.config.mute = !src.config.mute;

                    //
                    if (!src.config.mute) {
                        button.element.setAttribute('title', 'mute');
                        (button.elements.icon as any)?.removeClass('fa-solid fa-volume-mute');
                        (button.elements.icon as any)?.addClass('fa-solid fa-volume-up');
                    }
                    //
                    else {
                        button.element.setAttribute('title', 'unmute');
                        (button.elements.icon as any)?.removeClass('fa-solid fa-volume-up');
                        (button.elements.icon as any)?.addClass('fa-solid fa-volume-mute');
                    }
                });
            }

            // ----------------------------------------------------------------

            return {
                create,
            };
        })();

        // --------------------------------------------------------------------

        const framePerSecond = (function (): any {
            function create(config: TypeControlConfig = {}): void {
                // If not using frame per second
                if (isEmptyObject(config)) {
                    return;
                }

                // Init
                src.elements.fps = {};
                src.config.framePerSecond = defConfig.framePerSecond || 30;

                // Create element (fps)
                const elFramePorSecond = exElement.create({
                    merge: config,
                    class: {
                        new: 'control-fps',
                    },
                    attr: {
                        title: 'Frame Per Second',
                    },
                });
                src.element.appendChild(elFramePorSecond);
                src.elements.fps.element = elFramePorSecond;

                // ------------------------------------------------------------

                // Create element (button) (minus)
                const buttonMinus = exButton.create({
                    merge: config.minus,
                    icon: 'fa-solid fa-minus',
                });
                elFramePorSecond.appendChild(buttonMinus.element);
                src.elements.fps.minus = buttonMinus;

                // Create event (click)
                buttonMinus.element.addEventListener('click', function (): any {
                    if (src.config.framePerSecond > 1) {
                        --src.config.framePerSecond;
                    }

                    // Update
                    src.elements.fps.input.value = src.config.framePerSecond;

                    // If using function callback
                    if (typeof config.fnCallback === 'function') {
                        config.fnCallback({
                            fps: src.config.framePerSecond,
                        });
                    }
                });

                // ------------------------------------------------------------

                // If not using editor
                if (config.input?.editor === false) {
                    // Create element (text)
                    const elText = exElement.create({
                        merge: config.input,
                        class: {
                            new: 'fps-input',
                        },
                        html: src.config.framePerSecond,
                    });
                    elFramePorSecond.appendChild(elText);
                    src.elements.fps.input = elText;
                }
                // Is default
                else {
                    // Create element (input)
                    const input = exInput.create({
                        merge: config.input,
                        type: 'text',
                        class: {
                            add: 'fps-input',
                        },
                        attr: {
                            min: 1,
                            max: 99,
                        },
                        number: true,
                        value: src.config.framePerSecond,
                        event: {
                            input: function (evt: Event): any {
                                src.config.framePerSecond = (evt.target as HTMLInputElement).value;

                                // If using function callback
                                if (typeof config.fnCallback === 'function') {
                                    config.fnCallback({
                                        fps: src.config.framePerSecond,
                                    });
                                }
                            },
                        },
                    });
                    elFramePorSecond.appendChild(input.element);
                    src.elements.fps.input = input.element;
                }

                // ------------------------------------------------------------

                // Create element (button) (plus)
                const buttonPlus = exButton.create({
                    merge: config.plus,
                    icon: 'fa-solid fa-plus',
                });
                elFramePorSecond.appendChild(buttonPlus.element);
                src.elements.fps.plus = buttonPlus;

                // Create event (click)
                buttonPlus.element.addEventListener('click', function (): any {
                    if (src.config.framePerSecond < 100) {
                        ++src.config.framePerSecond;
                    }

                    // Update
                    src.elements.fps.input.value = src.config.framePerSecond;

                    // If using function callback
                    if (typeof config.fnCallback === 'function') {
                        config.fnCallback({
                            fps: src.config.framePerSecond,
                        });
                    }
                });
            }

            // ----------------------------------------------------------------

            return {
                create,
            };
        })();

        // --------------------------------------------------------------------

        function definition(config: TypeControlConfig = {}): void {
            // If not using definition
            if (isEmptyObject(config)) {
                return;
            }
        }

        function fullscreen(config: TypeControlConfig | boolean = {}): void {
            // If not using fullscreen
            if (config !== true) {
                return;
            }
        }

        // --------------------------------------------------------------------

        return {
            start,
        };
    })();

    // ------------------------------------------------------------------------

    function getValue(): any {
        return src.config.time;
    }

    function setValue(value: any): void {
        src.config.time = value;

        // If using range
        if (src.elements.range) {
            src.elements.range.setValue(value);
        }

        // If using text
        if (src.elements.time) {
            src.elements.time.setValue(value);
        }
    }

    // ------------------------------------------------------------------------

    const player = (function (): any {
        function play(): void {
            // Update IsPlaying
            src.config.isPlaying = true;

            // Update
            update();
        }

        function stop(): void {
            // Update IsPlaying
            src.config.isPlaying = false;

            // Update
            update();
        }

        function toggle(): void {
            // Update IsPlaying
            src.config.isPlaying = !src.config.isPlaying;

            // Update
            update();
        }

        function update(): void {
            // If is playing is false
            if (!src.config.isPlaying) {
                src.elements.play.element.setAttribute('title', exLanguage.line('controller.play'));
                src.elements.play.icon.removeClass('fa-solid fa-pause');
                src.elements.play.icon.addClass('fa-solid fa-play');
            }
            // If is playing is true
            else {
                src.elements.play.element.setAttribute('title', exLanguage.line('controller.pause'));
                src.elements.play.icon.removeClass('fa-solid fa-play');
                src.elements.play.icon.addClass('fa-solid fa-pause');
            }
        }

        // --------------------------------------------------------------------

        return {
            play,
            stop,
            toggle,
            update,
        };
    })();

    // ------------------------------------------------------------------------

    init(); // Ready

    return {
        element: src.element,
        elements: src.elements,
        play: player.play,
        stop: player.stop,
        toggle: player.toggle,
        getValue,
        setValue,
    };
};

// ----------------------------------------------------------------------------

export { start };
